import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import anime from 'animejs';
import {
  X,
  User,
  Building2,
  MapPin,
  Briefcase,
  Link,
  Mail,
  Loader2,
  UserPlus,
  Pencil,
  Check,
  Cloud,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import { useGlobalModal } from '../../context/GlobalModalContext';

// Debounce hook for autosave
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

interface FormData {
  name: string;
  email: string;
  headline: string;
  companyName: string;
  location: string;
  profileUrl: string;
  about: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  headline: '',
  companyName: '',
  location: '',
  profileUrl: '',
  about: '',
};

// Validation helpers — channel-agnostic: any profile/website URL, any email.
const isValidUrl = (url: string): boolean => {
  if (!url) return true; // Optional field
  try {
    new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
    return true;
  } catch {
    return false;
  }
};

const isValidEmail = (email: string): boolean => {
  if (!email) return true; // Optional field
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validateForm = (data: FormData): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Name validation
  if (!data.name.trim()) {
    errors.name = 'Name is required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  if (data.profileUrl && !isValidUrl(data.profileUrl)) {
    errors.profileUrl = 'Enter a valid URL';
  }
  if (data.email && !isValidEmail(data.email)) {
    errors.email = 'Enter a valid email address';
  }

  return errors;
};

export function NewLeadModal() {
  const { leadModal, closeLeadModal } = useGlobalModal();
  const queryClient = useQueryClient();

  // Track if we've auto-created a draft lead
  const [draftLeadId, setDraftLeadId] = useState<string | null>(null);
  const isCreatingDraft = useRef(false);

  // Edit mode if explicitly editing OR if we've created a draft
  const isEditMode = leadModal.mode === 'edit' || draftLeadId !== null;
  const editingLead = leadModal.editingLead;
  const activeLeadId = editingLead?.id || draftLeadId;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Refs for animations and focus management
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (leadModal.isOpen) {
      // Store the currently focused element
      lastFocusedElement.current = document.activeElement as HTMLElement;

      // Reset draft state for new modal
      setDraftLeadId(null);
      isCreatingDraft.current = false;

      // Apply edit data or prefill data
      if (leadModal.mode === 'edit' && editingLead) {
        setFormData({
          name: editingLead.name || '',
          email: (editingLead as { email?: string }).email || '',
          headline: editingLead.headline || '',
          companyName: editingLead.companyName || '',
          location: editingLead.location || '',
          profileUrl: editingLead.profileUrl || '',
          about: editingLead.about || '',
        });
      } else if (leadModal.prefillData) {
        setFormData({
          ...initialFormData,
          ...leadModal.prefillData,
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});

      // Animate in
      requestAnimationFrame(() => {
        if (overlayRef.current) {
          anime({
            targets: overlayRef.current,
            opacity: [0, 1],
            duration: 200,
            easing: 'easeOutQuad',
          });
        }

        if (modalRef.current) {
          anime({
            targets: modalRef.current,
            opacity: [0, 1],
            scale: [0.95, 1],
            translateY: [20, 0],
            duration: 350,
            easing: 'easeOutExpo',
          });

          // Animate form fields staggered
          anime({
            targets: modalRef.current.querySelectorAll('.form-field'),
            opacity: [0, 1],
            translateY: [12, 0],
            delay: anime.stagger(40, { start: 150 }),
            duration: 300,
            easing: 'easeOutQuad',
          });
        }

        // Focus first input after animation
        setTimeout(() => {
          firstInputRef.current?.focus();
        }, 200);
      });
    } else {
      // Restore focus when modal closes
      setTimeout(() => {
        lastFocusedElement.current?.focus();
      }, 100);
    }
  }, [leadModal.isOpen, leadModal.prefillData, isEditMode, editingLead]);

  // Handle escape key
  useEffect(() => {
    if (!leadModal.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [leadModal.isOpen]);

  // Focus trap
  useEffect(() => {
    if (!leadModal.isOpen || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleTab);
    return () => window.removeEventListener('keydown', handleTab);
  }, [leadModal.isOpen]);

  // Auto-create draft mutation (silent, creates lead in background)
  const createDraftMutation = useMutation({
    mutationFn: (data: FormData) => api.createLead(data),
    onSuccess: (result) => {
      setDraftLeadId(result.id);
      isCreatingDraft.current = false;
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      isCreatingDraft.current = false;
      setSaveStatus('error');
    },
  });

  // Autosave mutation for edit mode (silent, no animations)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const debouncedFormData = useDebounce(formData, 500);
  const hasChanges = useRef(false);

  const autosaveMutation = useMutation({
    mutationFn: (data: FormData) => {
      if (!activeLeadId) throw new Error('No lead to update');
      return api.updateLead(activeLeadId, data);
    },
    onMutate: () => {
      setSaveStatus('saving');
    },
    onSuccess: () => {
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      // Reset to idle after showing "Saved"
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      setSaveStatus('error');
    },
  });

  // Auto-create draft when user enters a name (for create mode only)
  useEffect(() => {
    if (leadModal.mode === 'edit') return; // Don't auto-create if explicitly editing
    if (!leadModal.isOpen) return;
    if (draftLeadId) return; // Already created
    if (isCreatingDraft.current) return; // Already creating
    if (!debouncedFormData.name.trim()) return; // No name yet

    // Validate before creating
    const validationErrors = validateForm(debouncedFormData);
    if (Object.keys(validationErrors).length > 0) return; // Don't create with invalid data

    // Create draft lead
    isCreatingDraft.current = true;
    setSaveStatus('saving');
    createDraftMutation.mutate(debouncedFormData);
  }, [debouncedFormData.name, leadModal.isOpen, leadModal.mode, draftLeadId]);

  // Autosave effect for edit mode (including auto-created drafts)
  useEffect(() => {
    if (!activeLeadId || !leadModal.isOpen) return;
    if (!hasChanges.current) return; // Skip initial load
    if (isCreatingDraft.current) return; // Skip while creating draft

    // Only save if form has actual data
    if (!debouncedFormData.name.trim()) return;

    // Validate before saving
    const validationErrors = validateForm(debouncedFormData);
    if (Object.keys(validationErrors).length > 0) return; // Don't save invalid data

    autosaveMutation.mutate(debouncedFormData);
  }, [debouncedFormData, activeLeadId, leadModal.isOpen]);

  // Track when user makes changes (after initial load) with inline validation
  const handleChange = (field: keyof FormData, value: string) => {
    hasChanges.current = true;
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    // Clear error if field is now valid, set error if invalid
    const fieldErrors = validateForm(newFormData);
    setErrors(prev => {
      const next = { ...prev };
      if (fieldErrors[field]) {
        next[field] = fieldErrors[field];
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const animateOut = useCallback((callback?: () => void) => {
    const timeline = anime.timeline({
      complete: callback,
    });

    if (modalRef.current) {
      timeline.add({
        targets: modalRef.current,
        opacity: [1, 0],
        scale: [1, 0.95],
        translateY: [0, 10],
        duration: 200,
        easing: 'easeInQuad',
      });
    }

    if (overlayRef.current) {
      timeline.add({
        targets: overlayRef.current,
        opacity: [1, 0],
        duration: 150,
        easing: 'easeInQuad',
      }, '-=150');
    }
  }, []);

  const handleClose = useCallback(() => {
    if (createDraftMutation.isPending || autosaveMutation.isPending) return;
    animateOut(closeLeadModal);
  }, [createDraftMutation.isPending, autosaveMutation.isPending, animateOut, closeLeadModal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // No explicit submit - everything autosaves
  };

  if (!leadModal.isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm"
        onClick={handleClose}
        style={{ opacity: 0 }}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-start justify-center pt-[12vh] px-4 pointer-events-none">
        <div
          ref={modalRef}
          className="relative w-full max-w-lg pointer-events-auto"
          style={{ opacity: 0 }}
        >
          {/* Ambient glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-transparent to-accent-500/20 rounded-3xl blur-3xl opacity-40 pointer-events-none" />

          {/* Modal content */}
          <div className="relative bg-slate-900/95 backdrop-blur-xl border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">
            {/* Top accent gradient */}
            <div className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-xl border border-white/[0.06] flex items-center justify-center",
                  isEditMode
                    ? "bg-gradient-to-br from-amber-500/20 to-orange-500/20"
                    : "bg-gradient-to-br from-primary-500/20 to-accent-500/20"
                )}>
                  {isEditMode ? (
                    <Pencil className="w-5 h-5 text-amber-400" />
                  ) : (
                    <UserPlus className="w-5 h-5 text-primary-400" />
                  )}
                </div>
                <div>
                  <h2 id="modal-title" className="text-lg font-semibold text-white">
                    {leadModal.mode === 'edit' ? 'Edit Lead' : 'New Lead'}
                  </h2>
                  <div className="flex items-center gap-1.5 text-xs">
                    {saveStatus === 'saving' && (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin text-slate-500" />
                        <span className="text-slate-500">Saving...</span>
                      </>
                    )}
                    {saveStatus === 'saved' && (
                      <>
                        <Check className="w-3 h-3 text-emerald-400" />
                        <span className="text-emerald-400">Saved</span>
                      </>
                    )}
                    {saveStatus === 'error' && (
                      <span className="text-red-400">Failed to save</span>
                    )}
                    {saveStatus === 'idle' && (
                      <>
                        <Cloud className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-500">Autosave on</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:text-white hover:bg-white/[0.06] transition-all"
                onClick={handleClose}
                disabled={createDraftMutation.isPending}
                aria-label="Close modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name - Required */}
              <div className="form-field" style={{ opacity: 0 }}>
                <label htmlFor="lead-name" className="block text-xs text-slate-500 uppercase tracking-wide mb-2">
                  Name <span className="text-red-400">*</span>
                </label>
                <div className="relative group">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-primary-400 transition-colors" />
                  <input
                    ref={firstInputRef}
                    id="lead-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Full name"
                    disabled={createDraftMutation.isPending}
                    className={cn(
                      'w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.04] border text-white text-sm',
                      'placeholder:text-slate-600',
                      'focus:border-primary-500/50 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-500/20',
                      'transition-all duration-200',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      errors.name ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' : 'border-white/[0.06]'
                    )}
                    autoComplete="off"
                  />
                  {errors.name && (
                    <p className="mt-1.5 text-xs text-red-400">{errors.name}</p>
                  )}
                </div>
              </div>

              {/* Headline / Title */}
              <div className="form-field" style={{ opacity: 0 }}>
                <label htmlFor="lead-headline" className="block text-xs text-slate-500 uppercase tracking-wide mb-2">
                  Title / Headline
                </label>
                <div className="relative group">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-primary-400 transition-colors" />
                  <input
                    id="lead-headline"
                    type="text"
                    value={formData.headline}
                    onChange={(e) => handleChange('headline', e.target.value)}
                    placeholder="VP of Sales, CEO, etc."
                    disabled={createDraftMutation.isPending}
                    className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white text-sm placeholder:text-slate-600 focus:border-primary-500/50 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    autoComplete="off"
                  />
                </div>
              </div>

              {/* Company & Location - Two columns */}
              <div className="form-field grid grid-cols-2 gap-3" style={{ opacity: 0 }}>
                <div>
                  <label htmlFor="lead-company" className="block text-xs text-slate-500 uppercase tracking-wide mb-2">
                    Company
                  </label>
                  <div className="relative group">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-primary-400 transition-colors" />
                    <input
                      id="lead-company"
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      placeholder="Company name"
                      disabled={createDraftMutation.isPending}
                      className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white text-sm placeholder:text-slate-600 focus:border-primary-500/50 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="lead-location" className="block text-xs text-slate-500 uppercase tracking-wide mb-2">
                    Location
                  </label>
                  <div className="relative group">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-primary-400 transition-colors" />
                    <input
                      id="lead-location"
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      placeholder="City, Country"
                      disabled={createDraftMutation.isPending}
                      className="w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white text-sm placeholder:text-slate-600 focus:border-primary-500/50 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="form-field" style={{ opacity: 0 }}>
                <label htmlFor="lead-email" className="block text-xs text-slate-500 uppercase tracking-wide mb-2">
                  Email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-primary-400 transition-colors" />
                  <input
                    id="lead-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="name@company.com"
                    disabled={createDraftMutation.isPending}
                    className={cn(
                      'w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.04] border text-white text-sm',
                      'placeholder:text-slate-600',
                      'focus:border-primary-500/50 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-500/20',
                      'transition-all duration-200',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      errors.email ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' : 'border-white/[0.06]'
                    )}
                    autoComplete="off"
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Profile / website URL — any channel (LinkedIn, company site, etc.) */}
              <div className="form-field" style={{ opacity: 0 }}>
                <label htmlFor="lead-source-url" className="block text-xs text-slate-500 uppercase tracking-wide mb-2">
                  Profile or website URL
                </label>
                <div className="relative group">
                  <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-primary-400 transition-colors" />
                  <input
                    id="lead-source-url"
                    type="url"
                    value={formData.profileUrl}
                    onChange={(e) => handleChange('profileUrl', e.target.value)}
                    placeholder="linkedin.com/in/…, company website, etc."
                    disabled={createDraftMutation.isPending}
                    className={cn(
                      'w-full h-11 pl-10 pr-3 rounded-xl bg-white/[0.04] border text-white text-sm',
                      'placeholder:text-slate-600',
                      'focus:border-primary-500/50 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-500/20',
                      'transition-all duration-200',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      errors.profileUrl ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' : 'border-white/[0.06]'
                    )}
                    autoComplete="off"
                  />
                  {errors.profileUrl && (
                    <p className="mt-1.5 text-xs text-red-400">{errors.profileUrl}</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="form-field" style={{ opacity: 0 }}>
                <label htmlFor="lead-notes" className="block text-xs text-slate-500 uppercase tracking-wide mb-2">
                  Notes
                </label>
                <textarea
                  id="lead-notes"
                  value={formData.about}
                  onChange={(e) => handleChange('about', e.target.value)}
                  placeholder="Any additional context..."
                  rows={3}
                  disabled={createDraftMutation.isPending}
                  className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white text-sm placeholder:text-slate-600 focus:border-primary-500/50 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* Error message */}
              {errors.submit && (
                <div className="form-field p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                  <p className="text-sm text-red-400">{errors.submit}</p>
                </div>
              )}

              {/* Actions - Always just a Done button (autosave handles everything) */}
              <div className="form-field flex items-center gap-3 pt-2" style={{ opacity: 0 }}>
                <Button
                  type="button"
                  className="flex-1 h-11 gap-2"
                  onClick={handleClose}
                  disabled={createDraftMutation.isPending}
                >
                  <Check className="w-4 h-4" />
                  Done
                  <kbd className="ml-2 hidden sm:inline-flex items-center text-[10px] text-white/40 bg-white/[0.06] px-1.5 py-0.5 rounded border border-white/[0.06]">
                    Esc
                  </kbd>
                </Button>
              </div>

              {/* Keyboard hint */}
              <div className="form-field flex items-center justify-center gap-4 pt-2 text-xs text-slate-600" style={{ opacity: 0 }}>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">Tab</kbd>
                  <span>to navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">Esc</kbd>
                  <span>when done</span>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
